import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatPrice } from "./shared";

export default function PhotographersManager() {
  const [filter, setFilter] = useState("");
  const [photographers, setPhotographers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/photographers${filter ? `?status=${filter}` : ""}`);
      setPhotographers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when the filter changes
  useEffect(() => { load(); }, [filter]);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/photographers/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const setCommission = async (id, rate) => {
    try {
      await api.put(`/admin/photographers/${id}`, { commission_rate: rate / 100 });
      toast.success(`Commission set to ${rate}%`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="photographers-manager">
      <div className="flex gap-3 flex-wrap">
        {["", "pending", "approved", "rejected", "suspended"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setFilter(s)}
            className={`text-xs uppercase tracking-[0.2em] px-4 py-2 font-semibold ${
              filter === s ? "bg-[var(--pm-orange)] text-black" : "border border-white/20 text-white/70 hover:border-white/60"
            }`}
            data-testid={`filter-${s || "all"}`}
          >
            {s || "All"}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-white/40 text-sm">Loading…</p>
      ) : photographers.length === 0 ? (
        <p className="text-white/50 text-sm py-8">No photographers match this filter.</p>
      ) : (
        <div className="space-y-4">
          {photographers.map((p) => (
            <div key={p.id} className="border-2 border-white/10 p-5 bg-[#0a0a0a]" data-testid={`photographer-${p.id}`}>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-display text-2xl uppercase">{p.company_name || p.name}</h3>
                    <StatusBadge status={p.status} />
                  </div>
                  <p className="text-sm text-white/60 font-condensed mt-1">
                    {p.name} · {p.email}{p.phone ? ` · ${p.phone}` : ""}
                  </p>
                  {p.website && (
                    <p className="text-sm mt-1"><a href={p.website.startsWith("http") ? p.website : `https://${p.website}`} target="_blank" rel="noreferrer" className="pm-orange hover:underline">{p.website}</a></p>
                  )}
                  {p.sample_url && (
                    <p className="text-sm mt-1"><a href={p.sample_url} target="_blank" rel="noreferrer" className="text-white/70 hover:pm-orange">Sample photos ↗</a></p>
                  )}
                  <div className="flex gap-6 mt-4 text-xs uppercase tracking-[0.2em] text-white/60 font-condensed">
                    <span>{p.photo_count || 0} photos</span>
                    <span>{p.sales_count || 0} sales</span>
                    <span>Earned: {formatPrice(p.total_earned || 0)}</span>
                    <span>Rate: <span className="pm-orange font-semibold">{Math.round((p.commission_rate || 0.15) * 100)}%</span></span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 items-start">
                  {p.status === "pending" && (
                    <>
                      <button onClick={() => setStatus(p.id, "approved")} className="text-xs uppercase tracking-[0.2em] bg-[var(--pm-orange)] text-black px-4 py-2 font-semibold hover:bg-[var(--pm-orange-bright)]" data-testid={`approve-${p.id}`}>Approve</button>
                      <button onClick={() => setStatus(p.id, "rejected")} className="text-xs uppercase tracking-[0.2em] border border-red-500 text-red-400 hover:bg-red-500/10 px-4 py-2 font-semibold" data-testid={`reject-${p.id}`}>Reject</button>
                    </>
                  )}
                  {p.status === "approved" && (
                    <button onClick={() => setStatus(p.id, "suspended")} className="text-xs uppercase tracking-[0.2em] border border-white/20 hover:border-red-400 text-white/70 hover:text-red-400 px-4 py-2 font-semibold" data-testid={`suspend-${p.id}`}>Suspend</button>
                  )}
                  {(p.status === "rejected" || p.status === "suspended") && (
                    <button onClick={() => setStatus(p.id, "approved")} className="text-xs uppercase tracking-[0.2em] bg-[var(--pm-orange)] text-black px-4 py-2 font-semibold" data-testid={`reinstate-${p.id}`}>Reinstate</button>
                  )}
                  <CommissionInput initial={Math.round((p.commission_rate || 0.15) * 100)} onSave={(v) => setCommission(p.id, v)} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    approved: "bg-[var(--pm-orange)] text-black",
    pending: "bg-yellow-500/20 text-yellow-300 border border-yellow-500/40",
    rejected: "bg-red-500/20 text-red-300 border border-red-500/40",
    suspended: "bg-white/10 text-white/60 border border-white/20",
  };
  return (
    <span className={`text-xs uppercase tracking-[0.2em] px-2 py-1 font-semibold ${map[status] || "bg-white/10"}`}>{status}</span>
  );
}

function CommissionInput({ initial, onSave }) {
  const [val, setVal] = useState(String(initial));
  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        min="0"
        max="100"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        className="w-16 text-center bg-transparent border border-white/20 py-1 focus:border-[var(--pm-orange)] outline-none text-sm"
        data-testid="commission-input"
      />
      <span className="text-xs text-white/40">%</span>
      <button
        onClick={() => onSave(Number(val))}
        className="text-xs uppercase tracking-[0.2em] px-3 py-1 border border-white/20 hover:border-[var(--pm-orange)] hover:pm-orange"
        data-testid="commission-save"
      >
        Save
      </button>
    </div>
  );
}
